'use client'

import { cn } from 'design-system'
import { ElementType, FC, ReactNode } from 'react'

import { GraduationCap } from 'lucide-react'
import { useFlags } from 'src/lib/feature-flags'
import { generateAvatarImageStyle } from 'src/lib/util/avatar'
import { AvatarButtonType, AvatarUser } from 'src/queryTypes/userTypes'

interface AvatarIconProps {
  size?: string
  onClick?: () => void
  isOnline?: boolean
  isAdmin?: boolean
  hasTooltip?: boolean
  buttonProps?: AvatarButtonType
  component?: ElementType
  className?: string
  children?: ReactNode
  useOriginalSrc?: boolean
  label?: string
  user: AvatarUser
}

const AvatarIcon: FC<AvatarIconProps> = ({
  user,
  size,
  useOriginalSrc,
  label,
  onClick,
  isOnline = false,
  isAdmin = false,
  hasTooltip = false,
  buttonProps,
  component: Component = 'span',
  className,
  children
}) => {
  const { gqlUserProfileLoading } = useFlags()
  const { id: userId, avatar, profilePhotoUrl } = user
  const userAvatar = gqlUserProfileLoading ? profilePhotoUrl : avatar

  const isButton = Component === 'button'

  const { ariaExpanded, ariaHaspopup, ...restButtonProps } = buttonProps || {}

  const avatarProps = gqlUserProfileLoading
    ? {
        src: userAvatar,
        className: 'object-cover block w-full h-full'
      }
    : generateAvatarImageStyle(userId, userAvatar, size, useOriginalSrc)

  return (
    <Component
      {...(isButton && { 'aria-label': label })}
      onClick={onClick}
      {...restButtonProps}
      {...(isButton &&
        ariaExpanded !== undefined && { 'aria-expanded': ariaExpanded })}
      {...(isButton &&
        ariaHaspopup !== undefined && { 'aria-haspopup': ariaHaspopup })}
      className={cn(
        'yd-user-avatar__img',
        {
          [`yd-user-avatar__img--${size}`]: !!size,
          'yd-user-avatar__img--clickable': isButton,
          'yd-user-avatar__img--admin': !!isAdmin,
          'yd-user-avatar__img--online': isOnline
        },
        className
      )}
      {...(hasTooltip && !isButton && { tabIndex: 0 })}>
      {children}
      {isAdmin ? (
        <span className="yd-user-avatar__admin">
          <GraduationCap
            size={16}
            className="stroke-1 text-yellow-600"
            aria-hidden="true"
          />
        </span>
      ) : null}
      <span className="block h-full w-full overflow-hidden rounded-full">
        <img {...avatarProps} aria-hidden="true" alt=""></img>
      </span>
    </Component>
  )
}

export default AvatarIcon
